import { memo } from "react";

import {
  AssignmentLinkCellRenderer,
  CampaignLinkCellRenderer,
} from "components/DataGrid/components";
import { BrandLinkCellRenderer } from "components/DataGrid/components/LinkCellRenderers/BrandLinkCellRenderer";
import { CreatorLinkCellRenderer } from "components/DataGrid/components/LinkCellRenderers/CreatorLinkCellRenderer";
import BoolCellRenderer from "components/DataGrid/customRenderers/BoolCellRenderer";
import CampaignStatusBadge from "modules/Campaigns/components/CampaignStatusBadge";

export const assignmentsColDefs = [
  { headerName: "_id", field: "_id", hide: true },
  {
    headerName: "Assignment",
    dataSource: "assignment",
    children: [
      {
        headerName: "Name",
        field: "_id",
        cellRenderer: AssignmentLinkCellRenderer,
        type: "text",
        initialWidth: 200,
        filterValueGetter: (params) => {
          // Makes the assignment's creator name, campaign name, and assignment ID searchable
          if (!params.data?.user_id?.name || !params.data?.campaign_id?.name) {
            return "";
          }
          return `${params.data?.user_id?.name} ${params.data?.campaign_id?.name} ${params.data?.assignment_id}`;
        },
        valueFormatter: (params) => {
          return `${params.data?.user_id?.name || "unknown"} <> ${params.data?.campaign_id?.name || "unknown"}`;
        },
      },
      {
        headerName: "Department",
        field: "department",
        type: "text",
        valueGetter: (params) => {
          let text = "";
          if (params.data?.department === "managed") {
            text = "Managed Services";
          }
          if (params.data?.department === "talent") {
            text = "Talent";
          }
          return text;
        },
      },
      {
        headerName: "Status",
        field: "status",
        type: "text",
      },
      {
        headerName: "Brand ID",
        field: "brand_id._id",
        dataSource: "brand",
        type: "text",
        hide: true,
        lockVisible: true,
      },
      {
        headerName: "Brand",
        field: "brand_id._id",
        dataSource: "brand",
        cellRenderer: BrandLinkCellRenderer,
        type: "text",
        filterValueGetter: (params) => {
          // Makes the brand's name and ID searchable
          if (!params.data?.brand_id?.name) {
            return "";
          }
          return `${params.data?.brand_id?.name} ${params.data?.brand_id?._id}`;
        },
        valueFormatter: (params) => {
          return params.data?.brand_id?.name || "";
        },
      },
      {
        headerName: "Custom Handle",
        field: "agencyCreatorName",
        type: "text",
      },
      {
        headerName: "Rate",
        field: "rate",
        type: "currency",
      },
      {
        headerName: "Allocated",
        field: "__allocatedBalance",
        type: "currency",
      },
      {
        headerName: "Unallocated",
        field: "__unallocatedBalance",
        type: "currency",
      },
      {
        headerName: "Hold",
        field: "holdBalance",
        type: "currency",
      },
      {
        headerName: "Withdrawable",
        field: "availableBalance",
        type: "currency",
      },
      {
        headerName: "Cashed Out",
        field: "paidBalance",
        type: "currency",
      },
      {
        headerName: "Expected Pay Date",
        field: "nextPayoutDate",
        type: "date",
      },
      {
        headerName: "Agreements Incomplete",
        field: "__agreementMetrics.requiredIncomplete",
        type: "number",
      },
      {
        headerName: "Agreements Complete",
        field: "__agreementMetrics.requiredComplete",
        type: "number",
      },
      {
        headerName: "Agreements Total",
        field: "__agreementMetrics.total",
        type: "number",
      },
    ],
  },
  {
    headerName: "Campaign",
    dataSource: "campaign",
    children: [
      {
        headerName: "ID",
        field: "campaign_id._id",
        type: "text",
        hide: true,
        lockVisible: true,
      },
      {
        headerName: "Name",
        field: "campaign_id._id",
        type: "text",
        dataSource: "campaign",
        cellRenderer: CampaignLinkCellRenderer,
        filterValueGetter: (params) => {
          // Makes the campaign's name and ID searchable
          if (!params.data?.campaign_id?.name) {
            return "";
          }
          return `${params.data?.campaign_id?.name} ${params.data?.campaign_id?._id}`;
        },
        valueFormatter: (params) => {
          return params.data?.campaign_id?.name || "";
        },
      },
      {
        headerName: "Short ID",
        field: "campaign_id._id",
        type: "text",
        dataSource: "campaign",
        cellRenderer: CampaignLinkCellRenderer,
        cellRendererParams: {
          linkTextKey: "campaign_id?.shortId",
        },
        filterValueGetter: (params) => {
          return params.data?.campaign_id?.shortId;
        },
        valueFormatter: (params) => {
          return params.data?.campaign_id?.shortId || "";
        },
      },
      {
        headerName: "Status",
        field: "campaign_id.status",
        type: "text",
        cellRenderer: memo(CampaignStatusBadge),
      },
      {
        headerName: "Segmented Account",
        field: "campaign_id.enableAccountSegmentation",
        type: "boolean",
        cellRenderer: BoolCellRenderer,
      },
      {
        headerName: "Addresses & Shipping",
        field: "campaign_id.isShippingCampaign",
        type: "boolean",
        cellRenderer: BoolCellRenderer,
      },
      // {
      //   // TODO: @backend - calculated field not returned when populating campaign_id
      //   headerName: "Unallocated",
      //   type: "currency",
      //   field: "campaign_id.unallocatedBalance",
      // },
      {
        headerName: "Budget",
        type: "currency",
        field: "campaign_id.creatorBudget",
      },
      {
        headerName: "Start Date",
        field: "campaign_id.startDate",
        type: "date",
      },
      {
        headerName: "End Date",
        field: "campaign_id.endDate",
        type: "date",
      },
    ],
  },
  {
    headerName: "Creator",
    dataSource: "creator",
    children: [
      // TODO: @coldefs review and add missing creator fields on assignment defs
      {
        headerName: "ID",
        field: "user_id._id",
        dataSource: "creator",
        type: "text",
        hide: true,
        lockVisible: true,
      },
      {
        headerName: "Name",
        field: "user_id._id",
        dataSource: "creator",
        cellRenderer: CreatorLinkCellRenderer,
        filterValueGetter: (params) => {
          // Makes the creator's name and ID searchable
          if (!params.data?.user_id?.name) {
            return "";
          }
          return `${params.data?.user_id?.name} ${params.data?.user_id?._id}`;
        },
        valueFormatter: (params) => {
          return params.data?.user_id?.name || "";
        },
      },
      {
        headerName: "Agency/Creator",
        field: "user_id.isAgency",
        type: "boolean",
        valueGetter: (params) => {
          return params.data?.user_id?.isAgency ? "Agency" : "Creator";
        },
      },
      {
        headerName: "Email",
        field: "user_id.email",
        type: "text",
        flex: 2,
      },
      {
        headerName: "Phone",
        field: "user_id.phone",
        // TODO: @cellrender add phone cell renderer
        type: "text",
      },
      {
        headerName: "Country",
        field: "user_id.country",
        type: "text",
      },
      {
        headerName: "Stripe Onboarding",
        field: "user_id.needsStripeOnboarding",
        type: "boolean",
        valueGetter: (params) => {
          return !params.data.user_id?.needsStripeOnboarding;
        },
        cellRenderer: BoolCellRenderer,
        cellRendererParams: {
          type: "icon",
          truthyText: "Complete",
          falsyText: "Not Completed",
        },
      },
      {
        headerName: "Financial Account",
        field: "user_id.needsStripeFinancialConnection",
        type: "boolean",
        cellDataType: "boolean", // BUG: this should be handled by type: "boolean" above, but boolean type is not applied when valueGetter is used
        valueGetter: (params) => {
          return !params.data.user_id?.needsStripeFinancialConnection;
        },
        cellRenderer: BoolCellRenderer,
        cellRendererParams: {
          type: "icon",
          truthyText: "Linked",
          falsyText: "Not Linked",
        },
      },
      {
        headerName: "Last Login",
        field: "user_id.lastLogin",
        type: "date",
      },
      // {
      //   headerName: "First Login",
      //   field: "user_id.firstLogin",
      //   type: "date",
      // },
      // {
      //   headerName: "Account Disabled",
      //   field: "user_id.disabled",
      //   type: "boolean",
      //   cellRenderer: WarningBoolCellRenderer,
      // },
    ],
  },
];
